import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fetchClientContext } from "../../api/clientContextApi";
import { clearLegacyUserCache, syncLegacyUserCache } from "../../common/helpers/auth/legacyUserCache";
import type { ClientContext } from "../../models/eplex/ClientContext";

type ContextStatus = "idle" | "loading" | "ready" | "error";

/**
 * Единственный источник признака демо-доступа на весь кабинет (CAB-567).
 *
 * Проверок «а вдруг триал» по компонентам не разводим: при снятии демо-доступа
 * часть интерфейса иначе осталась бы в прежнем состоянии, и это тот дефект,
 * который ищут неделю.
 */
export const useClientContextStore = defineStore("eplexClientContext", () => {
  const context = ref<ClientContext | null>(null);
  const status = ref<ContextStatus>("idle");

  let inflight: Promise<void> | null = null;

  /**
   * Пока контекст не загружен — демо-доступа НЕТ. Ошибиться в эту сторону
   * безопасно: боевому клиенту ничего не скроют. Ошибка в обратную сторону
   * закрыла бы заказы человеку, который за них платит.
   */
  const isTrial = computed(() => status.value === "ready" && context.value?.trial === true);

  /**
   * Кто именно сидит в этой вкладке: клиент, профиль и режим доступа. `null`,
   * пока сведений нет — сравнивать не с чем.
   *
   * Сессия кабинета — это кука домена `.eplex.ru`, общая для всех вкладок и
   * стендов. Вход другой учёткой где угодно рядом меняет её и под уже открытой
   * вкладкой: та продолжает работать от чужого имени, показывая прежние данные
   * и прежние признаки. Поэтому личность — отдельный наблюдаемый признак, а не
   * деталь загрузки контекста: по её смене перечитывается всё, что привязано к
   * человеку (CAB-734).
   */
  const identityKey = computed(() => {
    if (status.value !== "ready" || context.value === null) return null;

    const mode = context.value.trial === true ? "trial" : "live";

    return `${context.value.clientId ?? ""}:${context.value.profileId ?? ""}:${mode}`;
  });

  const clientName = computed(() => context.value?.clientName ?? "");
  const profileId = computed(() => context.value?.profileId ?? null);
  const signalHash = computed(() => context.value?.signalHash ?? null);
  const profileName = computed(() => context.value?.name ?? "");

  const isBlocked = computed(
    () =>
      status.value === "ready" &&
      (context.value?.profileBlocked === true ||
        context.value?.clientBlocked === true ||
        context.value?.eplexBlocked === true ||
        context.value?.clientActive === false),
  );

  async function load(force = false): Promise<void> {
    if (!force && (status.value === "ready" || status.value === "loading")) {
      await (inflight ?? Promise.resolve());
      return;
    }

    // Перечитывание под уже открытой страницей не отбрасывает кабинет в
    // «загружаем»: по статусу решается монтирование router-view (см.
    // MainLayout.isIdentityResolved), и заход через `loading` размонтировал бы
    // текущий экран вместе со всем его состоянием.
    const hadContext = status.value === "ready";
    if (!hadContext) status.value = "loading";

    inflight = (async () => {
      try {
        context.value = await fetchClientContext();
        // Опознание пользователя для кода, который читает `_user`: ключи
        // сохранённого состояния и текущий профиль в биллинге
        syncLegacyUserCache(context.value);
        status.value = "ready";
      } catch (error) {
        // Недоступность контекста не должна выкидывать человека из кабинета:
        // он работает, просто интерфейс не знает про демо-доступ
        console.error("Client context load error: ", error);
        // Сбой перечитывания — не повод забыть, кто вошёл: держим последние
        // достоверные сведения, следующая попытка их обновит
        status.value = hadContext ? "ready" : "error";
      } finally {
        inflight = null;
      }
    })();

    await inflight;
  }

  /** После перехода в рабочий режим (CAB-568) контекст перечитывается. */
  async function refresh(): Promise<void> {
    await load(true);
  }

  function reset(): void {
    context.value = null;
    status.value = "idle";
    inflight = null;
    clearLegacyUserCache();
  }

  return {
    context,
    status,
    isTrial,
    identityKey,
    clientName,
    profileName,
    profileId,
    signalHash,
    isBlocked,
    load,
    refresh,
    reset,
  };
});
